import { Plus, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { NOTIFICATION_EVENTS } from "@/modules/dashboard/notifications/constants/notifications.constants";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { AttachmentsGalleryModal } from "@/modules/dashboard/payments/components/AttachmentsGalleryModal";
import { Button } from "@/shared/components/form/Button";
import { ImageLightbox } from "@/shared/components/ImageLightbox";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { ClientNotifiedModal } from "../components/ClientNotifiedModal";
import { ConfirmationHistoryModal } from "../components/ConfirmationHistoryModal";
import { ConfirmationsBoard } from "../components/ConfirmationsBoard";
import { NewConfirmationModal } from "../components/NewConfirmationModal";
import { CONFIRMATIONS_PAGE_SIZE } from "../constants/confirmations.constants";
import { confirmationsService } from "../services/confirmations.service";
import type { ConfirmationUpdatedPayload } from "../types/confirmations-api.types";
import type { Confirmation } from "../types/confirmations.types";
import type { NewConfirmationFormData } from "../types/new-confirmation.types";
import {
  mapPaymentConfirmationToConfirmation,
  mapConfirmationUpdatedPayloadToConfirmation,
} from "../utils/mapConfirmationFromApi";

const ConfirmationsPage = () => {
  const { t } = useTranslations("confirmations");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_operaciones") },
      {
        label: tGlobal("sidebar_menu_confirmations"),
        to: "/app/confirmations",
      },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [isNewConfirmationModalOpen, setIsNewConfirmationModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImageUrls, setGalleryImageUrls] = useState<string[]>([]);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [galleryComprobanteIndex, setGalleryComprobanteIndex] = useState<number | undefined>(undefined);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyConfirmationId, setHistoryConfirmationId] = useState("");
  const [clientNotifiedModalOpen, setClientNotifiedModalOpen] = useState(false);
  const [clientNotifiedConfirmationId, setClientNotifiedConfirmationId] = useState("");
  const [pending, setPending] = useState<Confirmation[]>([]);
  const [confirmed, setConfirmed] = useState<Confirmation[]>([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchConfirmations = useCallback(async () => {
    try {
      setHasNoAccess(false);
      const response = await confirmationsService.fetchList({
        skip: 0,
        limit: CONFIRMATIONS_PAGE_SIZE,
      });
      const withoutNotified = response.data.filter((item) => item.status !== "NOTIFIED");
      const mapped = withoutNotified.map(mapPaymentConfirmationToConfirmation);
      setPending(mapped.filter((c) => c.status === "pending"));
      setConfirmed(mapped.filter((c) => c.status === "confirmed"));
      setSkip(response.data.length);
      setHasMore(response.hasMore ?? false);
    } catch (error) {
      if (HttpApiService.isAccessDeniedError(error)) {
        setHasNoAccess(true);
        return;
      }
      const message = HttpApiService.getErrorMessage(error);
      colocaToast.error(message || t("toast_error_load"));
    }
  }, [t]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const response = await confirmationsService.fetchList({
        skip,
        limit: CONFIRMATIONS_PAGE_SIZE,
      });
      const withoutNotified = response.data.filter((item) => item.status !== "NOTIFIED");
      const mapped = withoutNotified.map(mapPaymentConfirmationToConfirmation);
      const newPending = mapped.filter((c) => c.status === "pending");
      const newConfirmed = mapped.filter((c) => c.status === "confirmed");
      setPending((prev) => [...prev, ...newPending]);
      setConfirmed((prev) => [...prev, ...newConfirmed]);
      setSkip((prev) => prev + response.data.length);
      setHasMore(response.hasMore ?? false);
    } catch (error) {
      const message = HttpApiService.getErrorMessage(error);
      colocaToast.error(message || t("toast_error_load_more"));
    } finally {
      setIsLoadingMore(false);
    }
  }, [skip, hasMore, isLoadingMore, t]);

  useEffect(() => {
    fetchConfirmations();
  }, [fetchConfirmations]);

  useEffect(() => {
    const unsubscribe = notificationsSocketService.onNotification((notification, eventName) => {
      if (import.meta.env.VITE_ENV !== "production") {
        console.log("real_time confirmations:", eventName, notification);
      }
      if (eventName === NOTIFICATION_EVENTS.CONFIRMATION_NOTIFIED) {
        const payload = notification as unknown as ConfirmationUpdatedPayload;
        if (payload?.id) {
          setPending((prev) => prev.filter((c) => c.id !== payload.id));
          setConfirmed((prev) => prev.filter((c) => c.id !== payload.id));
        }
        return;
      }
      if (
        eventName === NOTIFICATION_EVENTS.CONFIRMATION_UPDATED ||
        eventName === NOTIFICATION_EVENTS.CONFIRMATION_TAKER_REMINDER
      ) {
        const payload = notification as unknown as ConfirmationUpdatedPayload;
        if (payload?.id) {
          if (eventName === NOTIFICATION_EVENTS.CONFIRMATION_TAKER_REMINDER && payload.status == null) {
            void fetchConfirmations();
            return;
          }
          const status = payload.status?.toUpperCase();
          if (status === "NOTIFIED") {
            setPending((prev) => prev.filter((c) => c.id !== payload.id));
            setConfirmed((prev) => prev.filter((c) => c.id !== payload.id));
            return;
          }
          const confirmation = mapConfirmationUpdatedPayloadToConfirmation(payload);
          setPending((prev) => prev.filter((c) => c.id !== confirmation.id));
          setConfirmed((prev) => prev.filter((c) => c.id !== confirmation.id));
          if (confirmation.status === "pending") {
            setPending((prev) => [confirmation, ...prev]);
          } else {
            setConfirmed((prev) => [confirmation, ...prev]);
          }
        }
        return;
      }
      const isConfirmationCreated =
        eventName === NOTIFICATION_EVENTS.CONFIRMATION_CREATED ||
        (notification as { type?: string })?.type === "CONFIRMATION_CREATED";
      if (isConfirmationCreated) {
        fetchConfirmations();
      }
    });
    return unsubscribe;
  }, [fetchConfirmations]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchConfirmations();
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchConfirmations]);

  const handleNewConfirmation = useCallback(() => {
    setIsNewConfirmationModalOpen(true);
  }, []);

  const handleCloseNewConfirmationModal = useCallback(() => {
    setIsNewConfirmationModalOpen(false);
  }, []);

  const handleSubmitNewConfirmation = useCallback(
    async (data: NewConfirmationFormData) => {
      try {
        await confirmationsService.create(data);
        colocaToast.success(t("toast_success_create"));
        handleCloseNewConfirmationModal();
        await fetchConfirmations();
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_create"));
        throw error;
      }
    },
    [handleCloseNewConfirmationModal, fetchConfirmations, t],
  );

  const handleMoveToConfirmed = useCallback((confirmation: Confirmation) => {
    void confirmation;
    // TODO: connect to API when available
  }, []);

  const handleCancel = useCallback(
    async (id: string, reason?: string) => {
      const note = reason?.trim() ?? "";
      if (!note) return;
      try {
        await confirmationsService.cancel(id, note);
        colocaToast.success(t("toast_success_cancel"));
        await fetchConfirmations();
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_cancel"));
        throw error;
      }
    },
    [fetchConfirmations, t],
  );

  const handleConfirm = useCallback(
    async (id: string, attachments?: File[], confirmation_source?: string) => {
      try {
        await confirmationsService.take(id);
        if (confirmation_source?.trim()) {
          await confirmationsService.updateConfirmation(id, {
            confirmation_source: confirmation_source.trim(),
          });
        }
        if (attachments?.length) {
          try {
            await confirmationsService.uploadAttachment(id, attachments);
          } catch (attachmentError) {
            const msg = HttpApiService.getErrorMessage(attachmentError);
            colocaToast.warning(msg || t("toast_warning_attachment"));
          }
        }
        colocaToast.success(t("toast_success_confirm"));
        await fetchConfirmations();
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_confirm"));
        throw error;
      }
    },
    [fetchConfirmations, t],
  );

  const handleViewHistory = useCallback((id: string) => {
    setHistoryConfirmationId(id);
    setHistoryModalOpen(true);
  }, []);

  const handleCloseHistoryModal = useCallback(() => {
    setHistoryModalOpen(false);
    setHistoryConfirmationId("");
  }, []);

  const handleView = useCallback((id: string, imageUrl?: string | string[] | null) => {
    void id;
    const urls = Array.isArray(imageUrl) ? imageUrl : imageUrl ? [imageUrl] : [];
    if (urls.length > 0) {
      setGalleryImageUrls(urls);
      setGalleryInitialIndex(0);
      setGalleryComprobanteIndex(0);
      setGalleryOpen(true);
    } else {
      setLightboxImageUrl(null);
      setLightboxOpen(true);
    }
  }, []);

  const handleNotifyClient = useCallback((id: string) => {
    setClientNotifiedConfirmationId(id);
    setClientNotifiedModalOpen(true);
  }, []);

  const handleCloseClientNotifiedModal = useCallback(() => {
    setClientNotifiedModalOpen(false);
    setClientNotifiedConfirmationId("");
  }, []);

  const handleConfirmClientNotified = useCallback(async () => {
    if (!clientNotifiedConfirmationId) return;
    try {
      await confirmationsService.notifyClient(clientNotifiedConfirmationId);
      colocaToast.success(t("toast_success_notify"));
      handleCloseClientNotifiedModal();
      await fetchConfirmations();
    } catch (error) {
      const message = HttpApiService.getErrorMessage(error);
      colocaToast.error(message || t("toast_error_notify"));
      throw error;
    }
  }, [clientNotifiedConfirmationId, handleCloseClientNotifiedModal, fetchConfirmations, t]);

  const handleTakerReminder = useCallback(
    async (id: string) => {
      try {
        await confirmationsService.sendTakerReminder(id);
        colocaToast.success(t("toast_success_taker_reminder"));
      } catch (error) {
        const message = HttpApiService.getErrorMessage(error);
        colocaToast.error(message || t("toast_error_taker_reminder"));
        throw error;
      }
    },
    [t],
  );

  const handleOpen = useCallback(
    (id: string, imageUrl?: string | string[] | null) => {
      void id;
      const url = Array.isArray(imageUrl) ? imageUrl[0] : imageUrl;
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
      } else {
        colocaToast.info(t("toast_no_image"));
      }
    },
    [t],
  );

  if (hasNoAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          {t("page_title")} (Payins)
        </h1>
        <Button
          type="button"
          variant="primary"
          icon={<Plus className="h-4 w-4" />}
          onClick={handleNewConfirmation}
          className="shadow-sm active:scale-[0.98] w-full sm:w-auto shrink-0"
        >
          {t("button_new_confirmation")}
        </Button>
      </div>

      <ConfirmationsBoard
        pending={pending}
        confirmed={confirmed}
        onMoveToConfirmed={handleMoveToConfirmed}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onViewHistory={handleViewHistory}
        onView={handleView}
        onOpen={handleOpen}
        onNotifyClient={handleNotifyClient}
        onTakerReminder={handleTakerReminder}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      {hasMore && (
        <div className="flex justify-center pt-2 pb-4">
          <button
            type="button"
            onClick={loadMore}
            disabled={isLoadingMore}
            className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-50 transition-colors"
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                Cargando…
              </>
            ) : (
              "Mostrar más"
            )}
          </button>
        </div>
      )}

      <NewConfirmationModal
        isOpen={isNewConfirmationModalOpen}
        onClose={handleCloseNewConfirmationModal}
        onSubmit={handleSubmitNewConfirmation}
      />

      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        imageUrl={lightboxImageUrl}
        alt={t("alt_comprobante_pago")}
      />

      <AttachmentsGalleryModal
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        imageUrls={galleryImageUrls}
        initialIndex={galleryInitialIndex}
        alt={t("alt_comprobante_confirmacion")}
        comprobanteIndex={galleryComprobanteIndex}
      />

      <ConfirmationHistoryModal
        isOpen={historyModalOpen}
        onClose={handleCloseHistoryModal}
        confirmationId={historyConfirmationId}
      />

      <ClientNotifiedModal
        isOpen={clientNotifiedModalOpen}
        onClose={handleCloseClientNotifiedModal}
        onConfirm={handleConfirmClientNotified}
      />
    </div>
  );
};

export { ConfirmationsPage };
export default ConfirmationsPage;
